<script setup lang="ts">
import type { PaginationData } from '~~/shared/types/store'

const props = defineProps<{
  currentPage: number
  totalPages: number
  totalItems: number
  previousPageUrl?: string | null
  nextPageUrl?: string | null
  pages?: PaginationData['pages']
}>()

const route = useRoute()

// Build page window: always show first, last, current ±2, with ellipsis gaps
const pages = computed(() => {
  const total = props.totalPages
  const cur = props.currentPage
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const result: (number | '...')[] = []
  const around = new Set([1, total, cur - 1, cur, cur + 1].filter(p => p >= 1 && p <= total))

  let prev = 0
  for (const p of [...around].sort((a, b) => a - b)) {
    if (p - prev > 1) result.push('...')
    result.push(p)
    prev = p
  }
  return result
})

function go(p: number | '...') {
  if (typeof p !== 'number' || p === props.currentPage) return
  // Nawigujemy bezpośrednio przez query param. Wcześniej komponent emitował
  // `update:currentPage`, a stronę nawigowała rodzic w inline handlerze
  // `@update:current-page="navigateTo(...)"` — w produkcyjnym buildzie ten
  // cache'owany inline handler nie wykonywał nawigacji (klik nie zmieniał URL).
  // navigateTo wywołane z metody komponentu działa niezawodnie (jak sort).
  navigateTo({ query: { ...route.query, page: p === 1 ? undefined : p } })
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>

<template>
  <nav v-if="totalPages > 1" class="pagination" aria-label="Paginacja">
    <button
      class="pagination__arrow"
      :disabled="currentPage <= 1"
      aria-label="Poprzednia strona"
      @click="go(currentPage - 1)"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M10 12L6 8L10 4" />
      </svg>
    </button>

    <button
      v-for="p in pages"
      :key="p"
      class="pagination__page"
      :class="{ 'pagination__page--active': p === currentPage, 'pagination__page--ellipsis': p === '...' }"
      :disabled="p === '...' || p === currentPage"
      :aria-current="p === currentPage ? 'page' : undefined"
      @click="go(p)"
    >
      {{ p }}
    </button>

    <button
      class="pagination__arrow"
      :disabled="currentPage >= totalPages"
      aria-label="Następna strona"
      @click="go(currentPage + 1)"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M6 4L10 8L6 12" />
      </svg>
    </button>
  </nav>
</template>

<style scoped>
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 32px 0;
}

.pagination__arrow,
.pagination__page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 4px;
  border: 1px solid transparent;
  background: none;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  transition: border-color var(--transition-fast), background var(--transition-fast);
  line-height: 1;
}

.pagination__arrow {
  border-color: var(--color-border-md);
  min-width: 36px;
}

.pagination__arrow:hover:not(:disabled) {
  background: var(--color-bg);
}

.pagination__arrow:disabled {
  opacity: 0.3;
  cursor: default;
}

.pagination__page:hover:not(:disabled):not(.pagination__page--active) {
  border-color: var(--color-border-md);
}

.pagination__page--active {
  border-color: var(--color-black);
  font-weight: var(--font-weight-normal);
  cursor: default;
}

.pagination__page--ellipsis {
  cursor: default;
  min-width: 24px;
}
</style>
